import React, { FC } from 'react';
import { Avatar, Typography, IconButton, TooltipV2 } from '@getgo/chameleon-web/react';
import classNames from 'classnames';
import { ReactComponent as Facebook } from './../../../svg/ic_facebook.svg';
import { ReactComponent as Linkedin } from './../../../svg/ic_linkedin.svg';
import { ReactComponent as Twitter } from './../../../svg/ic_twitter.svg';
import { GlobeIcon } from '@getgo/chameleon-icons/react';
import { IEventDetail } from '../eventInterface';
import { SpeakerUtils } from '../../utils/speakerUtils';
import RichTextContent from '../../utils/richTextContent/richTextContent';
import { useTranslation } from 'react-i18next';
import { onClickOfSpeakerSocialMediaLinkTracker } from '../../../mixpanel/Feature/eventTracker';

export interface IEventSpeakers {
  eventDetail: IEventDetail;
  activeSpeakerId: string;
  speakerListRef: { [index: string]: any };
  eventKey?: string;
}

const EventSpeakers: FC<IEventSpeakers> = ({
  eventDetail,
  activeSpeakerId,
  speakerListRef,
  eventKey,
}) => {
  const { t } = useTranslation();

  const socialMedialHandler = (socialMediaName: string, link: string) => {
    onClickOfSpeakerSocialMediaLinkTracker({ socialMediaName, link, eventKey });
  };
  const renderSocialMediaIcons = (link: string, id: string) => {
    switch (link) {
      case 'website':
        return (
          <div className="icon-box">
            <TooltipV2
              position={'bottom-center' as any}
              trigger={
                <IconButton variant="secondary" size="xsmall">
                  <GlobeIcon />
                </IconButton>
              }
            >
              <span>{t('event.addedSocialMedia.website')}</span>
            </TooltipV2>
          </div>
        );
      case 'facebook':
        return (
          <div className="icon-box">
            <TooltipV2
              position={'bottom-center' as any}
              trigger={
                <IconButton variant="secondary" size="xsmall">
                  <Facebook />
                </IconButton>
              }
            >
              <span>{t('event.addedSocialMedia.facebook')}</span>
            </TooltipV2>
          </div>
        );
      case 'twitter':
        return (
          <div className="icon-box">
            <TooltipV2
              position={'bottom' as any}
              trigger={
                <IconButton variant="secondary" size="xsmall">
                  <Twitter />
                </IconButton>
              }
            >
              <span>{t('event.addedSocialMedia.twitter')}</span>
            </TooltipV2>
          </div>
        );
      case 'linkedin':
        return (
          <div className="icon-box">
            <TooltipV2
              position={'bottom' as any}
              trigger={
                <IconButton variant="secondary" size="xsmall">
                  <Linkedin />
                </IconButton>
              }
            >
              <span>{t('event.addedSocialMedia.linkedin')}</span>
            </TooltipV2>
          </div>
        );
    }
  };
  return (
    <div className="event-speaker-box">
      {eventDetail?.speakers?.map((speaker: any) => (
        <div
          key={speaker.id}
          className={classNames('event-speaker', {
            selected: speaker.id === activeSpeakerId,
          })}
          ref={(element) => (speakerListRef.current[speaker.id] = element)}
        >
          <div className="event-speaker-avatar">
            {speaker?.imgsrc ? (
              <Avatar size="xxlarge" variant="primary">
                <img alt={speaker.name} src={speaker.imgsrc} />
              </Avatar>
            ) : (
              <Avatar size="xxlarge" variant="primary">
                {SpeakerUtils.getInitials(speaker.name)}
              </Avatar>
            )}
          </div>
          <div>
            <div id={`event-speaker-name-${speaker.id}`} className="line-break event-speaker-name">
              <Typography color="text-01" variant="heading-small">
                {speaker.name ? speaker.name : ''}
              </Typography>
            </div>
            <div
              id={`event-speaker-job-details-${speaker.id}`}
              className="line-break event-speaker-job-details"
            >
              <Typography color="text-02">
                {`${speaker.jobTitle ? speaker.jobTitle : ''}${
                  speaker.jobTitle && speaker.organization ? ', ' : ''
                }${speaker.organization ? speaker.organization : ''}`}
              </Typography>
            </div>
            <div className="event-speaker-description">
              <Typography color="text-01">
                <RichTextContent>{speaker.bio}</RichTextContent>
              </Typography>
            </div>
            <div className="event-hosted-by-details-icon">
              {['website', 'twitter', 'facebook', 'linkedin'].map(
                (socialMediaKey: string) =>
                  speaker.urls?.[socialMediaKey] && (
                    <a
                      target="_blank"
                      rel="noreferrer"
                      key={socialMediaKey}
                      onClick={() =>
                        socialMedialHandler(
                          socialMediaKey,
                          speaker.urls?.[socialMediaKey].indexOf('http://') > -1 ||
                            speaker.urls?.[socialMediaKey].indexOf('https://') > -1
                            ? speaker.urls?.[socialMediaKey]
                            : `http://${speaker.urls?.[socialMediaKey]}`,
                        )
                      }
                      href={
                        speaker.urls?.[socialMediaKey].indexOf('http://') > -1 ||
                        speaker.urls?.[socialMediaKey].indexOf('https://') > -1
                          ? speaker.urls?.[socialMediaKey]
                          : `http://${speaker.urls?.[socialMediaKey]}`
                      }
                      className="speaker-section-link"
                    >
                      {renderSocialMediaIcons(socialMediaKey, speaker.id)}
                    </a>
                  ),
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EventSpeakers;
